import { PropsWithChildren, useEffect, useMemo } from 'react';
import createSafeContext from '@paradigm/utils/createSafeContext';
import {
  LiquidityFill,
  LiquidityListReq,
  RawLiquidityFill,
} from '#/leaderboard/entities/liquidity';
import { processLiquidityFill } from '#/leaderboard/repositories/adapters';
import { UserFirm } from '#/leaderboard/entities/shared';
import { useStoredLeaderboardFetcher } from '#/leaderboard/contexts/fetcher';
import { listLiquidityFills } from '#/leaderboard/repositories/liquidity';
import {
  useLiquidityFilterActions,
  useLiquidityFilterState,
} from '#/leaderboard/contexts/liquidity-filter';

interface LiquidityFillsListState {
  readonly liquidityFills: LiquidityFill[];
  readonly userFirm: UserFirm | null;
  readonly status: 'loading' | 'idle' | 'error';
  readonly lastUpdatedAt: number | null;
  readonly error: string | null;
}
export const [useLiquidityFillsState, LiquidityFillsStateProvider] =
  createSafeContext<LiquidityFillsListState>('LiquidityFillsState');

export default function LiquidityFillsListProvider({
  children,
}: PropsWithChildren) {
  const filterActions = useLiquidityFilterActions();

  const {
    status,
    error,
    list,
    userFirm,
    lastUpdatedAt,
    fetchLeaderboard: fetchFillsList,
  } = useStoredLeaderboardFetcher<RawLiquidityFill, LiquidityListReq>({
    filterActions,
    fetchFn: listLiquidityFills,
    key: 'liquidity-fills',
  });

  const filters = useLiquidityFilterState();

  const state = useMemo(
    () => ({
      liquidityFills: list.map((v) => processLiquidityFill(v, userFirm)),
      status,
      lastUpdatedAt,
      error,
      userFirm,
    }),
    [list, status, lastUpdatedAt, error, userFirm],
  );

  useEffect(() => {
    fetchFillsList(filters);
  }, [fetchFillsList, filters]);

  return (
    <LiquidityFillsStateProvider value={state}>
      {children}
    </LiquidityFillsStateProvider>
  );
}
